import React from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import addMealPlanIcon from '@/assets/images/add-meal-plan-icon.svg';
import scheduleIcon from '@/assets/images/schedule-icon.svg';
import sendPaymentIcon from '@/assets/images/send-payment-icon.svg';
import { ClientDashboardItem } from '@/types/client';
import { Link, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button as ShadcnButton } from '@/components/ui/button';
import { getClientInvoiceStatusMeta } from './clientInvoiceStatus';
import {
  formatClientRestoreDeadline,
  getClientRecoverableUntil,
  isPendingDeletionClient,
} from '@/utils/client-account-deletion';

function formatOrdinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const value = n % 100;
  return n + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
}

function formatSessionDate(d: string | null | undefined): string {
  if (!d) return '—';
  const date = new Date(d);
  return `${formatOrdinal(date.getDate())} ${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
}

interface ClientCardProps {
  client: ClientDashboardItem;
  onReactivateClient?: (clientId: string) => void;
  onSuspendClient?: (clientId: string) => void;
  onScheduleClient?: (client: ClientDashboardItem) => void;
  onDeleteClient?: (client: ClientDashboardItem) => void;
  onClick?: (client: ClientDashboardItem) => void;
  onOpenPanel?: (clientId: string) => void;
  highlighted?: boolean;
}

type CardStatus = 'active' | 'suspended' | 'pending_deletion';

const statusStyles: Record<CardStatus, { label: string; color: string; bgColor: string; dotColor: string }> = {
  active: {
    label: 'Active',
    color: '#0ca678',
    bgColor: '#e8fbf3',
    dotColor: '#18bf88',
  },
  suspended: {
    label: 'Suspended',
    color: '#ef4444',
    bgColor: '#feecec',
    dotColor: '#ef4444',
  },
  pending_deletion: {
    label: 'Pending deletion',
    color: '#b45309',
    bgColor: '#fff7ed',
    dotColor: '#f59e0b',
  },
};

const miniInfoCardSx = {
  borderRadius: '10px',
  bgcolor: '#fff',
  border: '1px solid #e3e9ee',
  px: 1,
  py: 0.7,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

const miniInfoValueSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.84rem',
  fontWeight: 700,
  lineHeight: '20px',
  minHeight: 20,
  color: '#1b4f67',
} as const;

const miniInfoLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.75rem',
  lineHeight: '16px',
  color: '#8792a1',
} as const;

function normalizeStatus(status: string | undefined, isPendingDeletion = false): CardStatus {
  if (isPendingDeletion || status === 'pending_deletion') return 'pending_deletion';
  if (status === 'active') return 'active';
  if (status === 'suspended' || status === 'inactive' || status === 'archived') return 'suspended';
  return 'active';
}

function getClientsListStatusParam(searchParams: URLSearchParams): string | null {
  const status = searchParams.get('status');
  return status === 'active' || status === 'suspended' || status === 'draft' || status === 'pending_deletion' ? status : null;
}

export const ClientCard = ({
  client,
  onReactivateClient,
  onSuspendClient,
  onScheduleClient,
  onDeleteClient,
  onClick,
  onOpenPanel,
  highlighted = false,
}: ClientCardProps) => {
  const location = useLocation();

  const getClientInvoicesUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page') || '1';
    const limit = searchParams.get('limit') || '10';
    const status = getClientsListStatusParam(searchParams);
    let url = `/clients/${client.id}/details-v2/invoices?fromPage=${page}&fromLimit=${limit}`;
    if (status) url += `&fromStatus=${status}`;
    return url;
  };

  const getClientQuestionnairesUrl = () => {
    return '/questionnaires';
  };

  const getClientMealPlanUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page') || '1';
    const limit = searchParams.get('limit') || '10';
    const status = getClientsListStatusParam(searchParams);
    let url = `/clients/${client.id}/details-v2/meal-plan?fromPage=${page}&fromLimit=${limit}`;
    if (status) url += `&fromStatus=${status}`;
    return url;
  };

  const displayName = client.fullName || 'Unknown';
  const email = client.email?.trim() || '—';
  const locationDisplay = client.location?.trim() || '—';
  const lastSession = client.lastSessionDate;
  const upcomingSession = client.upcomingSessionDate;
  const qProgress = Math.max(0, Math.min(100, client.questionnaireProgress ?? 0));
  const questionnairesToReview = client.questionnairesSentCount || 0;
  const invoiceStatusMeta = getClientInvoiceStatusMeta(client.invoiceStatus);

  const cardStatus = normalizeStatus(client.status, isPendingDeletionClient(client));
  const statusStyle = statusStyles[cardStatus];
  const restoreUntil = formatClientRestoreDeadline(getClientRecoverableUntil(client));
  const isPaused = cardStatus === 'pending_deletion';

  const hasMenu =
    !isPaused && (
      (cardStatus === 'suspended' && !!onReactivateClient) ||
      (cardStatus === 'active' && !!onSuspendClient) ||
      !!onDeleteClient
    );

  return (
    <Box
      id={`client-card-${client.id}`}
      data-client-id={client.id}
      onClick={() => onClick?.(client)}
      sx={{
        borderRadius: '16px',
        border: highlighted ? '2px solid #d97706' : isPaused ? '2px solid #94a3b8' : '2px solid #34b6de',
        bgcolor: isPaused ? '#94a3b8' : '#34b6de',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: highlighted
          ? '0 0 0 4px rgba(217, 119, 6, 0.18)'
          : '0 1px 2px rgba(16, 24, 40, 0.04)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.18s ease, transform 0.18s ease',
        '&:hover': {
          boxShadow: highlighted
            ? '0 0 0 4px rgba(217, 119, 6, 0.22)'
            : '0 6px 18px rgba(16, 24, 40, 0.09)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: isPaused ? '#94a3b8' : '#34b6de',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            px: 1.1,
            py: 0.2,
            borderRadius: '999px',
            bgcolor: statusStyle.bgColor,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 'fit-content',
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: statusStyle.dotColor,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              fontWeight: 600,
              color: statusStyle.color,
              lineHeight: 1,
            }}
          >
            {statusStyle.label}
          </Typography>
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              px: 1.25,
              py: 0.28,
              borderRadius: '999px',
              bgcolor: 'rgba(255, 255, 255, 0.23)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.78rem',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.95)',
                whiteSpace: 'nowrap',
              }}
            >
              {questionnairesToReview === 0
                ? '0 Questionnaire sent'
                : `${questionnairesToReview} Questionnaire${questionnairesToReview !== 1 ? 's' : ''} sent`}
            </Typography>
          </Box>

          <Box
            onClick={(event) => {
              event.stopPropagation();
              onOpenPanel?.(client.id);
            }}
            sx={{
              width: 22,
              height: 18,
              borderRadius: '4px',
              bgcolor: 'rgba(255,255,255,0.42)',
              position: 'relative',
              flexShrink: 0,
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.6)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 9,
                height: '100%',
                borderRadius: '4px 0 0 4px',
                bgcolor: '#fff',
              },
            }}
          />

          {hasMenu ? (
            <Box onClick={(event) => event.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ShadcnButton variant="ghost" size="icon" className="h-6 w-6 rounded-full hover:bg-white/20">
                    <MoreVertRoundedIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </ShadcnButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {cardStatus === 'suspended' && onReactivateClient && (
                    <DropdownMenuItem onClick={() => onReactivateClient(client.id)}>
                      Reactivate Client
                    </DropdownMenuItem>
                  )}
                  {cardStatus === 'active' && onSuspendClient && (
                    <DropdownMenuItem onClick={() => onSuspendClient(client.id)}>Suspend Client</DropdownMenuItem>
                  )}
                  {!isPaused && onDeleteClient && (
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => onDeleteClient(client)}
                    >
                      Delete client
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </Box>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 0,
          px: 1.75,
          pt: 1.75,
          pb: 2.35,
          bgcolor: '#fff',
          borderTopLeftRadius: '14px',
          borderTopRightRadius: '14px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
        }}
      >
        <Typography
          component="div"
          sx={{
            width: '100%',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            p: 0,
            m: 0,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: { xs: '1.15rem', sm: '1.25rem' },
            lineHeight: 1.08,
            color: '#111827',
            letterSpacing: '-0.02em',
            cursor: 'pointer',
            '&:hover': { color: '#111827', textDecoration: 'underline' },
            textUnderlineOffset: '3px',
          }}
        >
          {displayName}
        </Typography>
        {isPaused ? (
          <Typography
            sx={{
              mt: 0.7,
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#b45309',
            }}
          >
            Deleted in companion app
            {restoreUntil ? ` · Restore until ${restoreUntil}` : ''}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 1.1,
            flexWrap: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {email !== '—' && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, minWidth: 0, flex: '1 1 auto' }}>
              <EmailOutlinedIcon sx={{ fontSize: 14, color: '#7d8795' }} />
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.8rem',
                  color: '#7d8795',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {email}
              </Typography>
            </Box>
          )}
          {locationDisplay !== '—' && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
              <LocationOnOutlinedIcon sx={{ fontSize: 14, color: '#7d8795' }} />
              <Typography
                sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#7d8795', whiteSpace: 'nowrap' }}
              >
                {locationDisplay}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 1.5,
            borderRadius: '12px',
            bgcolor: '#edf3f6',
            px: 1.2,
            py: 1.15,
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75 }}>
            <Box
              sx={{
                ...miniInfoCardSx,
                gridColumn: { xs: '1 / -1', sm: 'auto' },
              }}
            >
              <Typography sx={miniInfoValueSx}>
                {formatSessionDate(lastSession)}
              </Typography>
              <Typography sx={miniInfoLabelSx}>
                Last Session
              </Typography>
            </Box>
            <Box sx={miniInfoCardSx}>
              <Typography sx={miniInfoValueSx}>
                {formatSessionDate(upcomingSession)}
              </Typography>
              <Typography sx={miniInfoLabelSx}>
                Upcoming Session
              </Typography>
            </Box>
            <Box sx={miniInfoCardSx}>
              <Box sx={{ ...miniInfoValueSx, display: 'inline-flex', alignItems: 'center', gap: 0.45, color: invoiceStatusMeta.color }}>
                {invoiceStatusMeta.showIcon && invoiceStatusMeta.tone === 'paid' ? (
                  <CheckCircleOutlinedIcon sx={{ fontSize: 13, color: '#4cb9d8' }} />
                ) : null}
                <Typography sx={{ font: 'inherit', lineHeight: 'inherit', color: 'inherit' }}>
                  {invoiceStatusMeta.label}
                </Typography>
              </Box>
              <Typography sx={miniInfoLabelSx}>
                Invoice Status
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 1.15,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.65 }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#6b7684' }}>
                  Questionnaire Progress
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>
                  {qProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={qProgress}
                sx={{
                  height: 7,
                  borderRadius: '999px',
                  bgcolor: '#dce4ea',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: '999px',
                    bgcolor: '#24a7d5',
                  },
                }}
              />
            </Box>
            <Box
              component={Link}
              to={getClientQuestionnairesUrl()}
              onClick={(event) => event.stopPropagation()}
              aria-label={`Open questionnaires for ${displayName}`}
              sx={{
                width: 32,
                height: 36,
                borderRadius: '10px',
                bgcolor: '#ffffff',
                display: 'grid',
                placeItems: 'center',
                p: 0.5,
                flexShrink: 0,
                border: '1px solid #dce4ea',
                cursor: 'pointer',
                textDecoration: 'none',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  borderColor: '#c3d2de',
                  bgcolor: '#f8fbfd',
                },
              }}
            >
              <Box
                component="svg"
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                sx={{ display: 'block' }}
              >
                <path
                  opacity="0.5"
                  d="M2.00342 5.3426C2.00342 3.45384 2.00342 2.50946 2.59018 1.9227C3.17694 1.33594 4.12132 1.33594 6.01008 1.33594H10.0167C11.9055 1.33594 12.8499 1.33594 13.4366 1.9227C14.0234 2.50946 14.0234 3.45384 14.0234 5.3426V10.6848C14.0234 12.5736 14.0234 13.518 13.4366 14.1047C12.8499 14.6915 11.9055 14.6915 10.0167 14.6915H6.01008C4.12132 14.6915 3.17694 14.6915 2.59018 14.1047C2.00342 13.518 2.00342 12.5736 2.00342 10.6848V5.3426Z"
                  fill="#20AEDC"
                />
                <path fillRule="evenodd" clipRule="evenodd" d="M5.84297 1.34375V14.6985H4.84131V1.34375H5.84297Z" fill="#20AEDC" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0.834961 5.34263C0.834961 5.06603 1.05919 4.8418 1.33579 4.8418H2.67135C2.94795 4.8418 3.17218 5.06603 3.17218 5.34263C3.17218 5.61923 2.94795 5.84346 2.67135 5.84346H1.33579C1.05919 5.84346 0.834961 5.61923 0.834961 5.34263ZM0.834961 8.01374C0.834961 7.73714 1.05919 7.51291 1.33579 7.51291H2.67135C2.94795 7.51291 3.17218 7.73714 3.17218 8.01374C3.17218 8.29034 2.94795 8.51457 2.67135 8.51457H1.33579C1.05919 8.51457 0.834961 8.29034 0.834961 8.01374ZM0.834961 10.6848C0.834961 10.4082 1.05919 10.184 1.33579 10.184H2.67135C2.94795 10.184 3.17218 10.4082 3.17218 10.6848C3.17218 10.9615 2.94795 11.1857 2.67135 11.1857H1.33579C1.05919 11.1857 0.834961 10.9615 0.834961 10.6848Z"
                  fill="#20AEDC"
                />
                <path
                  d="M7.17871 4.34068C7.17871 4.06407 7.40294 3.83984 7.67954 3.83984H11.0184C11.295 3.83984 11.5193 4.06407 11.5193 4.34068C11.5193 4.61728 11.295 4.84151 11.0184 4.84151H7.67954C7.40294 4.84151 7.17871 4.61728 7.17871 4.34068Z"
                  fill="#20AEDC"
                />
                <path
                  d="M7.17871 6.6779C7.17871 6.40129 7.40294 6.17706 7.67954 6.17706H11.0184C11.295 6.17706 11.5193 6.40129 11.5193 6.6779C11.5193 6.9545 11.295 7.17873 11.0184 7.17873H7.67954C7.40294 7.17873 7.17871 6.9545 7.17871 6.6779Z"
                  fill="#20AEDC"
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {isPaused ? (
          <Box sx={{ mt: 1.15, mb: 0.35 }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#64748b' }}>
              Paused on your roster. They can restore from the companion app until the deadline. After that they are removed automatically.
            </Typography>
          </Box>
        ) : (
        <Box
          sx={{
            mt: 1.15,
            mb: 0.35,
            display: 'grid',
            gap: 0.9,
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          }}
        >
          <Button
            component={Link}
            to={getClientMealPlanUrl()}
            onClick={(event) => event.stopPropagation()}
            endIcon={<img src={addMealPlanIcon} alt="" width={14} height={14} style={{ display: 'block', opacity: 0.95 }} />}
            variant="outlined"
            sx={{
              minHeight: 38,
              borderRadius: '10px',
              borderColor: '#d3dbe4',
              color: '#64748b',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.82rem',
              fontWeight: 500,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '& .MuiButton-endIcon': {
                ml: 0.7,
                mr: 0,
              },
              '&:hover': {
                borderColor: '#b9c6d3',
                bgcolor: '#f7fafc',
              },
            }}
          >
            Add Meal Plan
          </Button>

          <Button
            onClick={(event) => {
              event.stopPropagation();
              onScheduleClient?.(client);
            }}
            endIcon={<img src={scheduleIcon} alt="" width={14} height={14} style={{ display: 'block', opacity: 0.95 }} />}
            variant="outlined"
            sx={{
              minHeight: 38,
              borderRadius: '10px',
              borderColor: '#d3dbe4',
              color: '#64748b',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.82rem',
              fontWeight: 500,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '& .MuiButton-endIcon': {
                ml: 0.7,
                mr: 0,
              },
              '&:hover': {
                borderColor: '#b9c6d3',
                bgcolor: '#f7fafc',
              },
            }}
          >
            Schedule
          </Button>

          <Button
            component={Link}
            to={getClientInvoicesUrl()}
            onClick={(event) => event.stopPropagation()}
            endIcon={<img src={sendPaymentIcon} alt="" width={14} height={14} style={{ display: 'block' }} />}
            variant="contained"
            sx={{
              minHeight: 38,
              borderRadius: '10px',
              bgcolor: '#25a8da',
              color: '#fff',
              border: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.82rem',
              fontWeight: 600,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              gridColumn: { xs: '1 / -1', sm: 'auto' },
              boxShadow: 'none',
              '& .MuiButton-endIcon': {
                ml: 0.7,
                mr: 0,
              },
              '&:hover': {
                bgcolor: '#1498ca',
                boxShadow: 'none',
              },
            }}
          >
            Invoices
          </Button>
        </Box>
        )}
      </Box>
    </Box>
  );
};
